// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CombinationChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import ChartHasThresholdOptionBuilder from './Izenda.ChartHasThresholdOptionBuilder';
import VISUAL_TYPE from 'share/components/charting/highcharts/Izenda.VisualTypeMapping';

import highchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import jsFormatService from 'share/Izenda.JsFormatService';
import { get, getOr, escape, assign, merge, isNil, filter, findIndex, find, assignIn, flow } from 'lodash/fp';
import { exportClass } from 'share/Izenda.ServiceProvider';

const Y_AXIS_CONTAINER_NAME = 'values';
@exportClass('CombinationChartOptionsBuilder')
export default class CombinationChartOptionsBuilder extends ChartHasThresholdOptionBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   * @returns {Object}
   */
  buildOptionsByType(visualType: string, userOptions: any, dataParser: any): any {
    let chartOptions = super.buildOptionsByType(visualType, userOptions, dataParser, false) || {};

    const yAxisContainerName = getOr(Y_AXIS_CONTAINER_NAME, 'Y_AXIS_CONTAINER_NAME', userOptions);

    const dataStructure = dataParser.dataStructure || {};
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const defaultColors = this.DefaultColors;

    userOptions.chart.type = '';

    const combinationSeries = chartOptions.series;
    const thresholdSeries = chartOptions.thresholdSeries;
    const { izSingleYAxis } = userOptions;
    const isMultiSeries = combinationSeries.length > 1;
    const doesTransformToSingleYAxis = izSingleYAxis && isMultiSeries && !this.chartOptions.isSetting;

    chartOptions.series = [...combinationSeries, ...thresholdSeries];

    /**
     * Get index of Y-axis field that threshold belongs to
     * @param {Object} thresholdSerie
     * @returns {number}
     */
    const getYAxisFieldOfThresholdSeries = (thresholdSerie: any) => {
      const fieldOptions = this.fieldOptions;
      const thresholdFieldNameAlias = getOr('', 'yAxisField.fieldNameAlias', thresholdSerie);

      const indexYAxisField = findIndex(combinationSerie => {
        const fieldNameAlias = combinationSerie.fieldNameAlias;
        const dynamicThresholds = fieldOptions[fieldNameAlias].dynamicThresholds;

        return find({ fieldName: thresholdFieldNameAlias }, dynamicThresholds);
      }, dataStructure[yAxisContainerName]);

      return indexYAxisField < 0 ? 0 : indexYAxisField;
    };

    setConfigSerie(userOptions, chartOptions, doesTransformToSingleYAxis);

    return chartOptions;

    /**
     * Set config for series
     * @param {Object} userOptions
     * @param {Object} chartOptions
     * @param {boolean} doesTransformToSingleYAxis
     */
    function setConfigSerie(userOptions: any, chartOptions: any, doesTransformToSingleYAxis: boolean): void {
      const orgYaxis = chartOptions.yAxis;
      const colors = isNil(userOptions.colors) ? defaultColors : userOptions.colors;
      chartOptions.yAxis = [];

      const lineSeries = filter(s => s.type === VISUAL_TYPE.Line || s.type === VISUAL_TYPE.Area, chartOptions.series);
      if (lineSeries.length > 0) {
        // Line and Area do not support colorByPoint.
        // So we need to get the colorByPoint which will be a flag to manually set color for point
        const colorByPoint = get('plotOptions.series.colorByPoint', userOptions);
        const colorOptions = {
          izMultiColor: colorByPoint,
          izDisableMultiColor: userOptions.izDisableMultiColor
        };
        highchartUtils.setColorPointForAreaLine(colorOptions, lineSeries, colors);

        const plotOptions = {
          series: {
            colorByPoint: false
          }
        };

        chartOptions.plotOptions = merge(chartOptions.plotOptions, plotOptions);
      }

      const buildYAxis = (index, axisConfig) => {
        let serie = chartOptions.series[index];
        const serieName = serie.name;
        const serieColor = serie.isThreshold ? null : colors[index];

        if (!serie.isThreshold) {
          // link to y-axis
          chartOptions.series[index] = flow(
            assignIn(get('chartOptions', izendaSeriesConfig[serieName])),
            assignIn({
              yAxis: index
            })
          )(serie);
        } else {
          const indexOfThreshold = getYAxisFieldOfThresholdSeries(serie);
          chartOptions.series[index] = flow(
            assignIn(get('chartOptions', izendaSeriesConfig[serieName])),
            assignIn({
              yAxis: indexOfThreshold,
              type: 'line'
            })
          )(serie);
        }

        let yAxisOptions = assignIn(orgYaxis, {
          title: {
            text: serieName,
            style: {
              color: serieColor
            }
          },
          labels: {
            formatter() {
              // Format value for combination chart
              const jsFormatString = get(
                'fieldElement.properties.dataFormattings.format.jsFormatString',
                izendaSeriesConfig[serieName]
              );
              if (jsFormatString) {
                const jsFormatId = get(
                  'fieldElement.properties.dataFormattings.format.formatId',
                  izendaSeriesConfig[serieName]
                );
                return jsFormatService.format(jsFormatId, this.value);
              }

              // as new requirement, label will not apply alternative text.
              return escape(this.value);
            },
            style: {
              color: serieColor
            }
          },
          opposite: !!index,
          plotLines: getSeriesConfigOption(index, 'threshold')
        });

        // Only create y-axis for each serie - not threshold
        if (!serie.isThreshold) {
          yAxisOptions = merge(yAxisOptions, getSeriesConfigOption(index, 'axisOption'));
          !!get('title', axisConfig) && (yAxisOptions.title = assign(yAxisOptions.title, axisConfig.title));
          chartOptions.yAxis.push(yAxisOptions);
        }
      };

      if (doesTransformToSingleYAxis) {
        const axisConfig = {
          title: {
            text: localizer.getText('COMMON_LABEL_ALL')
          }
        };

        buildYAxis(0, axisConfig);
      } else {
        for (let index = 0; index < chartOptions.series.length; index++) {
          buildYAxis(index);
        }
      }
    }

    /**
     * Get series config option by dataKey
     * @param {number} index
     * @param {string} dataKey
     * @returns {string}
     */
    function getSeriesConfigOption(index: number, dataKey: string): string {
      const yAxisFields = get(Y_AXIS_CONTAINER_NAME, dataStructure);
      if (yAxisFields && yAxisFields.length > index) {
        return get(yAxisFields[index].fieldNameAlias + '.' + dataKey, izendaSeriesConfig);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.CombinationChartOptionsBuilder.js